import React, { Component } from 'react';
import VehicleHighlightsSVG from './VehicleHighlightsSVG';
import { calculateAnimationState } from './utils/frameCalculator';

class ResponsiveVehicleHighlights extends Component {
  constructor(props) {
    super(props);
    this.state = {
      scaleFactor: 1,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1470,
      viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 835,
      headingKey: 0,
      prevHeadingVisible: false,
    };
    this.containerRef = React.createRef();
  }

  componentDidMount() {
    this.updateScale();
    window.addEventListener('resize', this.handleResize);
    
    // Use ResizeObserver to track container size changes
    if (this.containerRef.current) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateScale();
      });
      
      const container = this.containerRef.current.parentElement;
      if (container) {
        this.resizeObserver.observe(container);
      }
    }
  }

  componentDidUpdate(prevProps) {
    // Set up ResizeObserver if it wasn't set up in componentDidMount
    if (!this.resizeObserver && this.containerRef.current) {
      const container = this.containerRef.current.parentElement;
      if (container) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateScale();
        });
        this.resizeObserver.observe(container);
        this.updateScale();
      }
    }

    // Bump headingKey when heading transitions from invisible → visible so the
    // CSS slide-in animation replays each time the highlight zone is entered.
    if (prevProps.currentFrame !== this.props.currentFrame || prevProps.totalFrames !== this.props.totalFrames) {
      const { itemStates: prev } = calculateAnimationState(prevProps.currentFrame, prevProps.totalFrames);
      const { itemStates: curr } = calculateAnimationState(this.props.currentFrame, this.props.totalFrames);
      const wasVisible = (prev?.[0]?.opacity ?? 0) > 0;
      const isVisible  = (curr?.[0]?.opacity ?? 0) > 0;
      if (!wasVisible && isVisible) {
        this.setState(s => ({ headingKey: s.headingKey + 1 }));
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    
    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  getAspectRatioName = (ratio) => {
    if (ratio < 1.35) return '4:3';
    if (ratio < 1.55) return '3:2';
    if (ratio < 1.7) return '16:10';
    if (ratio < 1.85) return '16:9';
    return 'Wide';
  };

  handleResize = () => {
    this.updateScale();
  };

  updateScale = () => {
    const BASE_VIEWPORT_WIDTH = 1470;
    const BASE_VIEWPORT_HEIGHT = 835;
    const BASE_ASPECT_RATIO = BASE_VIEWPORT_WIDTH / BASE_VIEWPORT_HEIGHT; // ~1.76 (16:9-ish)
    const HIGHLIGHTS_BASE_HEIGHT = 780; // VehicleHighlightsSVG base height
    
    // Use container dimensions if available, otherwise fall back to window dimensions
    let viewportWidth = window.innerWidth;
    let viewportHeight = window.innerHeight;
    
    if (this.containerRef.current) {
      const container = this.containerRef.current.parentElement;
      if (container) {
        const rect = container.getBoundingClientRect();
        viewportWidth = rect.width;
        viewportHeight = rect.height;
      }
    }
    
    const viewportAspectRatio = viewportWidth / viewportHeight;
    
   
    let effectiveBaseWidth = BASE_VIEWPORT_WIDTH;
    
    if (viewportAspectRatio < 1.4) {
      effectiveBaseWidth = BASE_VIEWPORT_WIDTH * 0.85; // ~1250px
    } else if (viewportAspectRatio < 1.55) {
      effectiveBaseWidth = BASE_VIEWPORT_WIDTH * 0.92; // ~1350px
    }
    
    const widthScale = viewportWidth / effectiveBaseWidth;
    
    let verticalPadding;
    
    if (viewportAspectRatio < 1.4) {
      verticalPadding = viewportHeight < 700 ? 40 : 60;
    } else if (viewportAspectRatio < 1.55) {
      verticalPadding = viewportHeight < 750 ? 60 : 80;
    } else if (viewportAspectRatio < 1.7) {
      verticalPadding = viewportHeight < 800 ? 80 : 100;
    } else {
      // 16:9 and wider screens (≈1.78+) - reduce padding to show more content
      verticalPadding = viewportHeight < 800 ? 60 : 80;
    }
    
    const availableHeight = viewportHeight - verticalPadding;
    const heightScale = availableHeight / HIGHLIGHTS_BASE_HEIGHT;
    
    // Use the smaller scale to ensure it fits both dimensions
    let scaleFactor = Math.min(widthScale, heightScale);
    
    // Set minimum and maximum scale limits (allow down to 10% for very small screens)
    scaleFactor = Math.max(0.1, Math.min(scaleFactor, 2.0));
    
    let leftMargin = 40;
    let rightMargin = 20;
    let topMargin = 40;
    
    // Detect different screen sizes
    const isMicroTinyScreen = (viewportWidth >= 300 && viewportWidth < 360) && viewportHeight < 220;
    const isUltraTinyScreen = viewportWidth < 300 || viewportHeight < 200;
    
    const isShorterExtraTinyScreen = viewportWidth < 400 && viewportHeight < 250; // For 374×224 type screens
    const isExtraTinyScreen = viewportWidth < 400 || viewportHeight < 300; // For 378×284 type screens
    const isMediumWidthShortScreen = viewportWidth >= 400 && viewportWidth < 600 && viewportHeight < 300; // For 452×268 type screens
    const isSmallScreen = viewportWidth < 1000 || viewportHeight < 750;
    const isTinyScreen = viewportWidth < 800 || viewportHeight < 600;
    
    if (isMicroTinyScreen) {
      // Micro tiny screens (like 326×197) - balanced margins for slightly wider screens
      rightMargin = 28;
      leftMargin = 15;
      topMargin = 8;
      
    } else if (isUltraTinyScreen) {
      // Ultra tiny screens (like 287×175) - minimal margins to maximize space
      rightMargin = 8;
      leftMargin = 8;
      topMargin = 2;
      
    } else if (isShorterExtraTinyScreen) {
      // Shorter extra tiny screens (like 374×224) - compact height requires tighter vertical spacing
      rightMargin =32;
      leftMargin = 12;
      topMargin = 10;
      
    } else if (isMediumWidthShortScreen) {
      // Medium width short screens (like 452×268) - wider but short height
      rightMargin = 45;
      leftMargin = 15;
      topMargin = 12;
      
    } else if (isExtraTinyScreen) {
      // Extra tiny screens (like 378×284) - taller variant with more breathing room
      rightMargin = 12;
      leftMargin = 12;
      topMargin = 20;
      
    } else if (isTinyScreen) {
      // Very tiny screens - minimal margins
      leftMargin = 8;
      rightMargin = 5;
      topMargin = 15;
    } else if (isSmallScreen) {
      // Small screens - reduced margins
      leftMargin = 20;
      rightMargin = 10;
      topMargin = 25;
    } else if (viewportAspectRatio < 1.5) {
      // Medium 4:3 or 3:2 screens - moderate margins
      leftMargin = 30;
      rightMargin = 15;
      topMargin = 30;
    } else {
      // Standard and larger screens (16:9, 16:10, ultrawide) - use fixed base margins
      leftMargin = 40;
      rightMargin = 20;
      topMargin = 40;
    }

    const isWideAspectLike = viewportAspectRatio >= 1.6;

    if (isMediumWidthShortScreen && isWideAspectLike) {
      // Reduce side gaps for 16:9-ish medium short frames
      rightMargin = Math.min(rightMargin, 14);
      leftMargin = Math.min(leftMargin, 10);
    }

    if (isWideAspectLike) {
      // Cap horizontal margins on wide ratios to avoid large side gaps
      const maxRightMargin = Math.max(8, Math.round(viewportWidth * 0.05));
      const maxLeftMargin = Math.max(12, Math.round(viewportWidth * 0.06));
      rightMargin = Math.min(rightMargin, maxRightMargin);
      leftMargin = Math.min(leftMargin, maxLeftMargin);
    }

    if (isWideAspectLike && viewportWidth < 534) {
      // Tighten further below 534px to match 16:9 gap behavior
      const narrowRight = Math.max(4, Math.round(viewportWidth * 0.022));
      const narrowLeft = Math.max(6, Math.round(viewportWidth * 0.028));
      rightMargin = Math.min(rightMargin, narrowRight);
      leftMargin = Math.min(leftMargin, narrowLeft);
    }

    if (isWideAspectLike && viewportHeight < 301) {
      // Tighten margins for short 16:9 heights to avoid side gaps
      const shortRight = Math.max(4, Math.round(viewportWidth * 0.02));
      const shortLeft = Math.max(6, Math.round(viewportWidth * 0.025));
      rightMargin = Math.min(rightMargin, shortRight);
      leftMargin = Math.min(leftMargin, shortLeft);
    }
    
    const HIGHLIGHTS_BASE_WIDTH = 360;
    const scaledHighlightsWidth = HIGHLIGHTS_BASE_WIDTH * scaleFactor;
    const requiredWidth = leftMargin + scaledHighlightsWidth + rightMargin;
    
    if (requiredWidth > viewportWidth) {
      // Calculate maximum allowed scale to fit within viewport
      const availableWidth = viewportWidth - leftMargin - rightMargin;
      const maxWidthScale = availableWidth / HIGHLIGHTS_BASE_WIDTH;
      
      // Use the smaller scale factor
      scaleFactor = Math.min(scaleFactor, maxWidthScale);
      
      // Ensure we don't go below minimum
      scaleFactor = Math.max(0.1, scaleFactor);
    }
    
    // Calculate separate margins for vehicle header (left side)
    // Similar to highlights: smaller screens need MORE absolute padding for visibility
    let headerLeftMargin = 40;
    let headerTopMargin = 40;
    
    if (isMicroTinyScreen) {
      // Micro tiny screens (like 326×197) - moderate margins for slightly wider screens
      headerLeftMargin = 28;
      headerTopMargin = 12;
    } else if (isUltraTinyScreen) {
      // Ultra tiny screens (like 287×175) - minimal margins to fit everything
      headerLeftMargin = 10;
      headerTopMargin = 8;
    } else if (isShorterExtraTinyScreen) {
      // Shorter extra tiny screens (like 374×224) - compact height needs tighter spacing
      headerLeftMargin = 35;
      headerTopMargin = 15;
    } else if (isMediumWidthShortScreen) {
      // Medium width short screens (like 452×268) - wider screen needs comfortable margins
      headerLeftMargin = 45;
      headerTopMargin = 18;
    } else if (isExtraTinyScreen) {
      // Extra tiny screens (like 378×284) - taller variant with more space
      headerLeftMargin = 25;
      headerTopMargin = 22;
    } else if (isTinyScreen) {
      // Very tiny screens - LARGER margins for visibility (matches highlights philosophy)
      headerLeftMargin = viewportWidth < 400 ? 20 : 22;
      headerTopMargin = viewportHeight < 300 ? 15 : 18;
    } else if (isSmallScreen) {
      // Small screens - moderate margins
      headerLeftMargin = viewportWidth < 700 ? 25 : 28;
      headerTopMargin = viewportHeight < 600 ? 30 : 32;
    } else if (viewportAspectRatio < 1.5) {
      // 4:3 or 3:2 screens - comfortable margins
      headerLeftMargin = 30;
      headerTopMargin = 35;
    } else {
      // Standard and larger screens - base margins
      headerLeftMargin = 40;
      headerTopMargin = 40;
    }

    if (isMediumWidthShortScreen && isWideAspectLike) {
      headerLeftMargin = Math.min(headerLeftMargin, 18);
    }

    if (isWideAspectLike) {
      const maxHeaderLeft = Math.max(12, Math.round(viewportWidth * 0.06));
      headerLeftMargin = Math.min(headerLeftMargin, maxHeaderLeft);
      headerTopMargin = Math.min(headerTopMargin, 28);
    }

    if (isWideAspectLike && viewportWidth < 534) {
      const narrowHeaderLeft = Math.max(6, Math.round(viewportWidth * 0.028));
      headerLeftMargin = Math.min(headerLeftMargin, narrowHeaderLeft);
    }

    if (isWideAspectLike && viewportHeight < 301) {
      const shortHeaderLeft = Math.max(6, Math.round(viewportWidth * 0.025));
      headerLeftMargin = Math.min(headerLeftMargin, shortHeaderLeft);
    }
    
    // Calculate vertical offset adjustment for ultra-tiny/micro-tiny screens to prevent bottom cropping
    // On very small height screens, shift highlights up from center
    let highlightsVerticalOffset = 0;
    if (isMicroTinyScreen && viewportHeight < 210) {
      // Micro tiny: smaller shift for slightly larger heights
      highlightsVerticalOffset = -5;
    } else if (isUltraTinyScreen && viewportHeight < 200) {
      // Ultra tiny: shift up more for very small heights
      highlightsVerticalOffset = -8;
    }
    
    this.setState({
      scaleFactor,
      viewportWidth,
      viewportHeight,
      viewportAspectRatio,
      leftMargin,
      rightMargin,
      topMargin,
      headerLeftMargin,
      headerTopMargin,
      highlightsVerticalOffset,
      isSmallScreen,
    });
  };

  render() {
    const {
      scaleFactor,
      leftMargin = 40,
      rightMargin = 20,
      topMargin = 40,
      headerLeftMargin = 40,
      headerTopMargin = 40,
      highlightsVerticalOffset = 0,
      viewportAspectRatio = 1.76,
      isSmallScreen = false,
      viewportWidth = 1470,
      viewportHeight = 835,
      headingKey = 0,
    } = this.state;
    const {
      currentFrame,
      totalFrames,
      enabled,
      vehicleData,
      specItemsConfig,
      debug,
    } = this.props;

    if (!enabled) {
      return null;
    }

    // Get animation state for vehicle header
    const animationState = calculateAnimationState(currentFrame, totalFrames);
    const { itemStates } = animationState;
    const headerState = itemStates && itemStates[0] ? itemStates[0] : { opacity: 0, translateX: -200 };

    // Extract vehicle info
    const make = vehicleData?.make || '';
    const model = vehicleData?.modelName || '';
    const trim = vehicleData?.trim || '';
    const year = vehicleData?.year || '';

    return (
      <>
      <style>{`
        @keyframes rvhHeadingSlideInLeft {
          from { opacity: 0; transform: translateX(-40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <div
        ref={this.containerRef}
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 10,
          // Ensure overflow is visible to prevent cropping
          overflow: 'visible',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '60%',
            background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(168,168,168,0) 68.8%, rgba(153,153,153,0) 100%)',
            opacity: 0.7,
            pointerEvents: 'none',
            zIndex: -1,
          }}
        />
        
        {/* Vehicle Header - Left Side */}
        <div
          style={{
            position: 'absolute',
            left: `${headerLeftMargin}px`,
            top: `${headerTopMargin}px`,
            opacity: headerState.opacity,
            transform: `scale(${scaleFactor})`,
            transformOrigin: 'left top',
            pointerEvents: 'none',
            zIndex: 11,
            maxWidth: `calc(50% - ${headerLeftMargin}px)`,
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
        >
          {/* Inner wrapper re-mounts on each entry, replaying the CSS slide animation */}
          <div
            key={headingKey}
            style={{ animation: 'rvhHeadingSlideInLeft 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}
          >
          {/* Make */}
          <div
            style={{
              fontSize: '52px',
              fontWeight: '700',
              color: '#FFFFFF',
              fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
              letterSpacing: '1px',
              textShadow: '2px 2px 8px rgba(0,0,0,0.5)',
              lineHeight: '1.1',
              marginBottom: '0px',
              textAlign: 'left',
              paddingLeft: '0px',
            }}
          >
            {make.toUpperCase()}
          </div>
          
          {/* Model + Trim + Year (Make is already shown in first line) */}
          <div
            style={{
              fontSize: '28px',
              fontWeight: '400',
              color: '#FFFFFF',
              fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
              letterSpacing: '0.5px',
              textShadow: '1px 1px 6px rgba(0,0,0,0.5)',
              lineHeight: '1.2',
              marginTop: '2px',
              textAlign: 'left',
              paddingLeft: '0px',
            }}
          >
            {`${model.toUpperCase()} ${trim.toUpperCase()} ${year.toUpperCase()}`}
          </div>
          </div>{/* end inner animation wrapper */}
        </div>{/* end outer heading wrapper */}

        {/* Vehicle Highlights - Right Side (responsive to aspect ratio) */}
        <div
          style={{
            position: 'absolute',
            right: `${rightMargin}px`,
            top: '50%',
            transform: `translateY(calc(-50% + ${highlightsVerticalOffset}px)) scale(${scaleFactor})`,
            transformOrigin: 'right center',
            transition: 'transform 0.2s ease-out',
            pointerEvents: 'none',
            // Ensure overflow is visible to prevent cropping on small screens
            overflow: 'visible',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
        >
          <VehicleHighlightsSVG
            currentFrame={currentFrame}
            totalFrames={totalFrames}
            enabled={enabled}
            vehicleData={vehicleData}
            specItemsConfig={specItemsConfig}
            debug={debug}
          />
        </div>

        {/* Debug scale info */}
        {debug && (
          <div
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: 'rgba(0,0,0,0.7)',
              color: '#fff',
              padding: '10px',
              fontSize: '12px',
              fontFamily: 'monospace',
              borderRadius: '4px',
              pointerEvents: 'none',
              zIndex: 999,
            }}
          >
            <div>Viewport: {viewportWidth} × {viewportHeight}</div>
            <div>Aspect: {viewportAspectRatio.toFixed(2)} ({this.getAspectRatioName(viewportAspectRatio)})</div>
            <div>Scale: {scaleFactor.toFixed(3)}x</div>
            <div>Highlights: L{Math.round(leftMargin)} R{Math.round(rightMargin)} T{Math.round(topMargin)}</div>
            <div>Header: L{Math.round(headerLeftMargin)} T{Math.round(headerTopMargin)}</div>
            <div>Small Screen: {isSmallScreen ? 'Yes' : 'No'}</div>
          </div>
        )}
      </div>
      </>
    );
  }
}

export default ResponsiveVehicleHighlights;
